import { getQuantityLabel } from '@/utils/nativeQuantity';
import type {
    UserPortfolioPosition,
    TradeTransactionWithFinancials,
    PortfolioCapitalBasis,
    EntityOptionMetadata,
    Entity,
    EntityObjectCompressed,
    EntityOptionObjectCompressed
} from '@einhaus/project314-types';
import { getTradeTotalCostSigned } from '@einhaus/project314-shared';
import { calculatePercentageChangeFast, capitalizeFirstLetter, getDate, trimDecimals } from '@einhaus/meteoric-helpers';
import { getOpenPositionPriceImpactBasis, getOpenPositionRiskCapital } from '@/utils/tradingHelpers.tsx';
import { calculateSellToOpenEntryArorc } from '@/utils/calculateSellToOpenEntryArorc';
import type {
    CapitalBasisOption,
    ClosingTradeType,
    LinkedTradingAccountRow,
    PortfolioBalanceSummary,
    PortfolioLimitNotice,
    PortfolioWithAccountingFields,
    PositionTableData
} from './types';
import { capitalBasisLabels } from './constants';

export const formatCurrency = (value?: number | null) => {
    if (value === null || value === undefined) return 'N/A';
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const firstFiniteNumber = (...values: (number | null | undefined)[]) => {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
    }

    return null;
};

export const getPortfolioBalanceSummary = (portfolio: PortfolioWithAccountingFields | null): PortfolioBalanceSummary => {
    const balanceSummary: Partial<PortfolioBalanceSummary> = portfolio?.balanceSummary ?? {};

    const currentEquity =
        firstFiniteNumber(
            balanceSummary.currentEquity,
            (portfolio?.positionsValue ?? 0) + (portfolio?.cashBalance ?? 0),
            portfolio?.cashBalance
        ) ?? 0;

    const reportingCapitalBase = firstFiniteNumber(balanceSummary.reportingCapitalBase, portfolio?.cashBalance) ?? 0;
    const effectiveReportingCapitalBase =
        firstFiniteNumber(balanceSummary.effectiveReportingCapitalBase, reportingCapitalBase) ?? reportingCapitalBase;
    const netExternalFunding = firstFiniteNumber(balanceSummary.netExternalFunding) ?? 0;
    const cashLedgerBalance = firstFiniteNumber(balanceSummary.cashLedgerBalance, portfolio?.cashBalance) ?? 0;
    const markedOpenPositionValue = firstFiniteNumber(balanceSummary.markedOpenPositionValue, portfolio?.positionsValue) ?? 0;
    const lastClosingEquity = firstFiniteNumber(balanceSummary.lastClosingEquity, portfolio?.lastClosingValue) ?? currentEquity;
    const todayChange = firstFiniteNumber(balanceSummary.todayChange, currentEquity - lastClosingEquity) ?? 0;

    const todayChangePercent =
        firstFiniteNumber(balanceSummary.todayChangePercent, calculatePercentageChangeFast(lastClosingEquity, currentEquity)) ?? 0;

    const realizedPnl = firstFiniteNumber(balanceSummary.realizedPnl) ?? 0;
    const unrealizedPnl = firstFiniteNumber(balanceSummary.unrealizedPnl) ?? 0;
    const totalPnl = firstFiniteNumber(balanceSummary.totalPnl, realizedPnl + unrealizedPnl) ?? 0;
    const performanceEquity = firstFiniteNumber(balanceSummary.performanceEquity, currentEquity) ?? currentEquity;
    const brokerageEquity = firstFiniteNumber(balanceSummary.brokerageEquity);
    const brokerExecutionCapacity = firstFiniteNumber(balanceSummary.brokerExecutionCapacity);
    const allocationShortfall = firstFiniteNumber(balanceSummary.allocationShortfall, performanceEquity - currentEquity) ?? 0;

    return {
        reportingCapitalBase,
        effectiveReportingCapitalBase,
        cashLedgerBalance,
        markedOpenPositionValue,
        investedAmount: balanceSummary.investedAmount ?? null,
        uninvestedBudget: balanceSummary.uninvestedBudget ?? null,
        valuationAsOf: balanceSummary.valuationAsOf ?? '',
        dailyBaselineAsOf: balanceSummary.dailyBaselineAsOf ?? null,
        currentEquity,
        performanceEquity,
        lastClosingEquity,
        todayChange,
        todayChangePercent,
        netExternalFunding,
        realizedPnl,
        unrealizedPnl,
        totalPnl,
        brokerageEquity,
        brokerExecutionCapacity,
        brokerageDataAsOf: balanceSummary.brokerageDataAsOf ?? null,
        isBrokerageDataStale: balanceSummary.isBrokerageDataStale ?? false,
        allocationShortfall,
        isAllocationLimited: balanceSummary.isAllocationLimited ?? allocationShortfall >= 0.01
    };
};

export const getPositionMarketValue = (
    position: Pick<
        UserPortfolioPosition,
        | 'status'
        | 'quantity'
        | 'contractMultiplier'
        | 'currentPrice'
        | 'currentValue'
        | 'derivativeType'
        | 'openTransactionType'
        | 'direction'
    >
) => {
    if (position.status !== 'open' || position.quantity <= 0) return 0;

    if (position.derivativeType === 'Option' && position.openTransactionType === 'sellToOpen') {
        return trimDecimals(-(position.quantity * position.currentPrice * position.contractMultiplier));
    }

    if (position.derivativeType === null && position.direction === 'short') {
        return trimDecimals(-(position.quantity * position.currentPrice * position.contractMultiplier));
    }

    return trimDecimals(position.currentValue || position.quantity * position.currentPrice * position.contractMultiplier);
};

export const getTradeTransactionsNetCashFlow = (transactions?: TradeTransactionWithFinancials[]) => {
    if (!transactions?.length) return 0;

    return trimDecimals(
        transactions.reduce((runningTotal, transaction) => {
            return runningTotal + getTradeTotalCostSigned(Math.abs(transaction.value), transaction.type);
        }, 0)
    );
};

export const getPositionTotalPnl = (config: {
    position: Pick<
        UserPortfolioPosition,
        | 'derivativeType'
        | 'openTransactionType'
        | 'currentPrice'
        | 'currentValue'
        | 'quantity'
        | 'contractMultiplier'
        | 'soldSharesValue'
        | 'startingValue'
    >;
    marketValue: number;
    transactions?: TradeTransactionWithFinancials[];
}) => {
    const { position, marketValue, transactions } = config;

    if (position.derivativeType === 'Option' && position.openTransactionType === 'sellToOpen') {
        if (transactions?.length) {
            return trimDecimals(getTradeTransactionsNetCashFlow(transactions) + marketValue);
        }

        return trimDecimals(position.startingValue - position.quantity * position.currentPrice * position.contractMultiplier);
    }

    return trimDecimals(position.currentValue + position.soldSharesValue - position.startingValue);
};

export const isOpenShortOptionPosition = (
    position: Pick<UserPortfolioPosition, 'status' | 'derivativeType' | 'openTransactionType'>
): boolean => {
    return position.status === 'open' && position.derivativeType === 'Option' && position.openTransactionType === 'sellToOpen';
};

export const isOptionPosition = (position: Pick<UserPortfolioPosition, 'derivativeType'>): boolean => {
    return position.derivativeType === 'Option';
};

export const isLongOptionPosition = (position: Pick<UserPortfolioPosition, 'derivativeType' | 'openTransactionType'>): boolean => {
    return position.derivativeType === 'Option' && position.openTransactionType === 'buyToOpen';
};

export const isShortStockPosition = (position: Pick<UserPortfolioPosition, 'derivativeType' | 'direction'>): boolean => {
    return position.derivativeType === null && position.direction === 'short';
};

export const isShortExposurePosition = (
    position: Pick<UserPortfolioPosition, 'status' | 'derivativeType' | 'openTransactionType' | 'direction'>
): boolean => {
    return isOpenShortOptionPosition(position) || isShortStockPosition(position);
};

export const getPositionQuantity = (position: Pick<UserPortfolioPosition, 'quantity'>) => {
    return position.quantity;
};

export const getPositionQuantityLabel = (
    position: Pick<UserPortfolioPosition, 'entityType' | 'derivativeType'>
): PositionTableData['quantityLabel'] => {
    return getQuantityLabel(position);
};

export const getPositionPremiumReceived = (
    position: Pick<UserPortfolioPosition, 'status' | 'derivativeType' | 'openTransactionType' | 'startingValue'>
) => {
    if (!isOpenShortOptionPosition(position)) return null;

    return trimDecimals(Math.max(position.startingValue, 0));
};

export const normalizeAllocationCapitalBasis = (capitalBasis?: PortfolioCapitalBasis | null): PortfolioCapitalBasis =>
    capitalBasis ?? 'cash';

export const hasBrokerCapitalAllocationAccount = (config: {
    linkedTradingAccount: LinkedTradingAccountRow | null;
    portfolio: Pick<PortfolioWithAccountingFields, 'tradingAccountId'> | null;
}) => {
    const { linkedTradingAccount, portfolio } = config;

    return portfolio?.tradingAccountId != null && linkedTradingAccount?.id === portfolio.tradingAccountId;
};

export const getPortfolioAllocatedAmountForBasis = (config: {
    tradingAccount: LinkedTradingAccountRow;
    portfolioId: number;
    capitalBasis: PortfolioCapitalBasis;
}) => {
    const { tradingAccount, portfolioId, capitalBasis } = config;

    return tradingAccount.portfolioAllocations.reduce((runningTotal, allocation) => {
        if (allocation.portfolioId !== portfolioId) return runningTotal;
        if (normalizeAllocationCapitalBasis(allocation.capitalBasis) !== capitalBasis) return runningTotal;

        return allocation.type === 'allocate' ? runningTotal + allocation.amount : runningTotal - allocation.amount;
    }, 0);
};

export const getEnabledCapitalBasisSet = (availableCapitalBasisOptions: CapitalBasisOption[]) => {
    return new Set(availableCapitalBasisOptions.filter((option) => !option.disabled).map((option) => option.value));
};

export const getConfiguredAllocatedCapitalBasis = (config: {
    enabledCapitalBasis: Set<PortfolioCapitalBasis>;
    linkedTradingAccount: LinkedTradingAccountRow | null;
    portfolio: PortfolioWithAccountingFields | null;
}) => {
    const { enabledCapitalBasis, linkedTradingAccount, portfolio } = config;

    const configuredCapitalBasis =
        portfolio?.capitalProfile?.primaryExecutionCapitalBasis ?? portfolio?.capitalProfile?.reportingBaseCapitalBasis;

    if (!configuredCapitalBasis || !enabledCapitalBasis.has(configuredCapitalBasis) || !linkedTradingAccount || !portfolio) return null;

    const configuredAllocatedAmount = getPortfolioAllocatedAmountForBasis({
        tradingAccount: linkedTradingAccount,
        portfolioId: portfolio.id,
        capitalBasis: configuredCapitalBasis
    });

    return configuredAllocatedAmount > 0 ? configuredCapitalBasis : null;
};

export const getLargestAllocatedCapitalBasis = (config: {
    enabledCapitalBasis: Set<PortfolioCapitalBasis>;
    linkedTradingAccount: LinkedTradingAccountRow | null;
    portfolio: PortfolioWithAccountingFields | null;
}) => {
    const { enabledCapitalBasis, linkedTradingAccount, portfolio } = config;

    if (!linkedTradingAccount || !portfolio) return null;

    return (
        linkedTradingAccount.capitalBasisSummaries
            .filter((summary) => enabledCapitalBasis.has(summary.capitalBasis))
            .map((summary) => ({
                capitalBasis: summary.capitalBasis,
                allocatedAmount: getPortfolioAllocatedAmountForBasis({
                    tradingAccount: linkedTradingAccount,
                    portfolioId: portfolio.id,
                    capitalBasis: summary.capitalBasis
                })
            }))
            .filter((summary) => summary.allocatedAmount > 0)
            .sort((firstSummary, secondSummary) => secondSummary.allocatedAmount - firstSummary.allocatedAmount)[0]?.capitalBasis ?? null
    );
};

export const getDefaultAllocationCapitalBasis = (config: {
    availableCapitalBasisOptions: CapitalBasisOption[];
    linkedTradingAccount: LinkedTradingAccountRow | null;
    portfolio: PortfolioWithAccountingFields | null;
}) => {
    const { availableCapitalBasisOptions, linkedTradingAccount, portfolio } = config;
    const enabledCapitalBasis = getEnabledCapitalBasisSet(availableCapitalBasisOptions);

    return (
        getConfiguredAllocatedCapitalBasis({ enabledCapitalBasis, linkedTradingAccount, portfolio }) ??
        getLargestAllocatedCapitalBasis({ enabledCapitalBasis, linkedTradingAccount, portfolio }) ??
        availableCapitalBasisOptions.find((option) => !option.disabled)?.value ??
        'cash'
    );
};

export const getPortfolioLimitNotices = (config: {
    tradingAccount: LinkedTradingAccountRow;
    portfolioId: number;
}): PortfolioLimitNotice[] => {
    const { tradingAccount, portfolioId } = config;
    const capitalSnapshot = tradingAccount.brokerCapitalSnapshot;
    const permissionsSnapshot = tradingAccount.brokerPermissionsSnapshot;
    const notices: PortfolioLimitNotice[] = [];
    const accountEquity = firstFiniteNumber(capitalSnapshot?.equity, tradingAccount.equity);
    const broaderAvailableFunds = firstFiniteNumber(capitalSnapshot?.availableFunds, capitalSnapshot?.buyingPower, accountEquity);

    const cashLikeAvailableFunds = firstFiniteNumber(
        capitalSnapshot?.buyingPowerNonMarginableTrade,
        capitalSnapshot?.availableFundsNonMarginableTrade,
        capitalSnapshot?.cashBalance,
        tradingAccount.cashBalance
    );

    const dayTradingBuyingPower = capitalSnapshot?.dayTradingBuyingPower ?? null;

    for (const summary of tradingAccount.capitalBasisSummaries) {
        if (summary.overAllocatedAmount <= 0) continue;

        notices.push({
            id: `over-allocated-${summary.capitalBasis}`,
            title: `${capitalBasisLabels[summary.capitalBasis]} allocation exceeds broker capacity`,
            message: `${formatCurrency(summary.allocatedAmount)} is allocated against ${formatCurrency(
                summary.totalCapacity
            )} of current capacity. New entries are limited by live brokerage equity and capacity until the allocation is reduced.`,
            tone: 'warning'
        });
    }

    const latestWithdrawalReconciliation = [...tradingAccount.portfolioAllocations]
        .filter((allocation) => allocation.portfolioId === portfolioId && allocation.source === 'brokerageWithdrawal')
        .sort((first, second) => second.dateTime.localeCompare(first.dateTime))[0];

    if (latestWithdrawalReconciliation?.providerTransactionId) {
        const reconciledAmount = tradingAccount.portfolioAllocations.reduce((total, allocation) => {
            if (allocation.portfolioId !== portfolioId) return total;
            if (allocation.providerTransactionId !== latestWithdrawalReconciliation.providerTransactionId) return total;
            if (allocation.source !== 'brokerageWithdrawal' || allocation.type !== 'deallocate') return total;
            return total + allocation.amount;
        }, 0);

        notices.push({
            id: `brokerage-withdrawal-${latestWithdrawalReconciliation.providerTransactionId}`,
            title: 'Allocation reduced after brokerage withdrawal',
            message: `${formatCurrency(reconciledAmount)} was automatically deallocated on ${latestWithdrawalReconciliation.dateTime}. Deposits do not automatically increase the target allocation.`,
            tone: 'info'
        });
    }

    if (permissionsSnapshot?.marginMode === 'cash') {
        notices.push({
            id: 'cash-account',
            title: 'Cash account only',
            message:
                'This brokerage account is cash-only. Margin-backed allocations and day-trading leverage are unavailable until the broker enables margin.',
            tone: 'info'
        });
    }

    if (permissionsSnapshot?.isPatternDayTrader === true) {
        notices.push({
            id: 'broker-day-trader-flag',
            title: 'Broker day-trader flag reported',
            message: `The broker still reports this account with a day-trader flag. Project314 treats this as broker telemetry during the intraday-margin transition; order placement is governed by broker approval, trade-specific margin, and live buying power. Intraday buying power is currently ${formatCurrency(dayTradingBuyingPower)}.`,
            tone: 'info'
        });
    }

    if (cashLikeAvailableFunds !== null && broaderAvailableFunds !== null && broaderAvailableFunds - cashLikeAvailableFunds > 1) {
        notices.push({
            id: 'cash-like-capacity-gap',
            title: 'Cash-like capacity is lower than total buying power',
            message: `Only ${formatCurrency(cashLikeAvailableFunds)} is currently available for cash-like or non-marginable trades, even though broader available funds or buying power are ${formatCurrency(broaderAvailableFunds)}. Recent deposits, settlement timing, or broker restrictions can keep cash-secured trades capped below total account value.`,
            tone: 'info'
        });
    }

    if (tradingAccount.brokerage === 'Schwab' && permissionsSnapshot?.maxSupportedStrategyClass === null) {
        notices.push({
            id: 'schwab-approval-opacity',
            title: 'Schwab options approval is partially opaque',
            message:
                'Schwab does not expose option approval level or uncovered-option eligibility through this API. Project314 can estimate capacity, but Schwab may still reject a strategy at order placement.',
            tone: 'info'
        });
    }

    return notices;
};

export const getPositionFetchTargets = (positions: UserPortfolioPosition[]) => {
    const entitiesMap = new Map<string, EntityObjectCompressed>();
    const optionsMap = new Map<string, EntityOptionObjectCompressed>();

    for (const position of positions) {
        const entityKey = `${position.entityId}_${position.entityType}`;

        if (!entitiesMap.has(entityKey)) {
            entitiesMap.set(entityKey, {
                i: position.entityId,
                t: position.entityType === 'Asset' ? 'a' : position.entityType === 'Cryptocurrency' ? 'c' : 'i'
            });
        }

        if (
            (position.entityType === 'Asset' || position.entityType === 'AssetIndex') &&
            position.derivativeType === 'Option' &&
            position.derivativeId
        ) {
            const optionKey = `${position.derivativeId}_${position.entityId}_${position.entityType}`;

            if (!optionsMap.has(optionKey)) {
                optionsMap.set(optionKey, {
                    oId: position.derivativeId,
                    eId: position.entityId,
                    eType: position.entityType
                });
            }
        }
    }

    return {
        entitiesToFetch: Array.from(entitiesMap.values()),
        optionsToFetch: Array.from(optionsMap.values())
    };
};

export const buildPositionsTableData = (config: {
    portfolio: PortfolioWithAccountingFields | null;
    positions: UserPortfolioPosition[];
    entities: Entity[];
    entitiesToFetchCount: number;
    optionsToFetchCount: number;
    optionsData: { options: EntityOptionMetadata[] } | undefined;
}): PositionTableData[] => {
    const { portfolio, positions, entities, entitiesToFetchCount, optionsToFetchCount, optionsData } = config;

    if (!portfolio || positions.length === 0) return [];

    if (entitiesToFetchCount > 0 && !entities.length) {
        return [];
    }

    if (optionsToFetchCount > 0 && !optionsData) {
        return [];
    }

    const tableData: PositionTableData[] = [];

    for (const position of positions) {
        const entity = entities.find((e) => e.id === position.entityId && e.entityType === position.entityType);
        if (!entity) continue;

        const option = position.derivativeId
            ? optionsData?.options.find(
                  (o) => o.id === position.derivativeId && o.assetId === position.entityId && o.entityType === position.entityType
              )
            : null;
        const tradeForPosition = portfolio.trades?.find((trade) => trade.id === position.tradeId);
        const positionName = `${entity.symbol} ${entity.name}`;

        let itmState = '';

        if (position.derivativeType === 'Option' && option) {
            const isCall = option.type === 'c';
            const underlyingPrice = entity.lastPrice || 0;
            const strikePrice = option.strike || 0;

            if (isCall) {
                itmState = underlyingPrice > strikePrice ? 'ITM' : 'OTM';
            } else {
                itmState = underlyingPrice < strikePrice ? 'ITM' : 'OTM';
            }
        }

        const sharesValue = trimDecimals(position.currentPrice * position.quantity * position.contractMultiplier);
        const startingValue = position.startingValue || position.quantity * position.entryPrice * position.contractMultiplier;
        const currentValue = position.currentValue || position.quantity * position.currentPrice * position.contractMultiplier;
        const marketValue = getPositionMarketValue(position);
        const premiumReceived = getPositionPremiumReceived(position);
        const isOption = isOptionPosition(position);
        const isLongOption = isLongOptionPosition(position);
        const isOpenShortOption = isOpenShortOptionPosition(position);
        const isShortStock = isShortStockPosition(position);
        const isShortExposure = isShortExposurePosition(position);
        const positionTransactions =
            tradeForPosition?.transactions.filter(
                (transaction) => transaction.legId === position.legId && transaction.legNumber === position.legNumber
            ) ?? [];

        const entryArorc = isOpenShortOption
            ? calculateSellToOpenEntryArorc({
                  transactions: positionTransactions,
                  options: option ? [option] : []
              })
            : null;

        const totalPnl = getPositionTotalPnl({
            position: {
                derivativeType: position.derivativeType,
                openTransactionType: position.openTransactionType,
                currentPrice: position.currentPrice,
                currentValue,
                quantity: position.quantity,
                contractMultiplier: position.contractMultiplier,
                soldSharesValue: position.soldSharesValue,
                startingValue
            },
            marketValue,
            transactions: tradeForPosition?.transactions ?? undefined
        });

        const totalValue = startingValue + totalPnl;
        const percentageChange = calculatePercentageChangeFast(startingValue, totalValue);

        const rawPercentageChangeToday = calculatePercentageChangeFast(getOpenPositionPriceImpactBasis(position), position.currentPrice);

        const percentPriceImpactToday = isShortExposure ? -rawPercentageChangeToday : rawPercentageChangeToday;
        const totalValueToday = currentValue + position.soldSharesValue;

        const percentageValueChangeToday = calculatePercentageChangeFast(position.dayStartingValue || startingValue, totalValueToday);

        const riskCapital = getOpenPositionRiskCapital({
            position,
            transactions: tradeForPosition?.transactions ?? []
        });

        const date = position.insertDateTime ? capitalizeFirstLetter(getDate({ format: 'readable1', date: position.insertDateTime })) : '';

        tableData.push({
            id: position.id,
            strategyId: position.strategyId,
            name: positionName,
            optionBuyingStrategy: position.optionBuyingStrategy?.name || '',
            direction: position.direction,
            underlyingBias: position.underlyingBias,
            backtestId: position.backtestId ?? undefined,
            entryPrice: position.entryPrice,
            entryArorc,
            currentPrice: position.currentPrice,
            underlyingPrice: entity.lastPrice || undefined,
            quantity: getPositionQuantity(position),
            quantityLabel: getPositionQuantityLabel(position),
            contractMultiplier: position.contractMultiplier,
            sharesValue,
            startingValue,
            currentValue,
            marketValue,
            premiumReceived,
            riskCapital,
            profit: totalPnl,
            totalPnl,
            percentPriceImpactToday,
            percentChangeValueToday: percentageValueChangeToday,
            percentChange: percentageChange,
            date,
            transactions: null,
            entityId: entity.id,
            entityType: entity.entityType,
            isOption,
            isLongOption,
            isOpenShortOption,
            isShortStock,
            isShortExposure,
            option,
            itmState
        });
    }

    return tableData;
};

export const isOptionsStylePosition = (position: UserPortfolioPosition): boolean => {
    return position.openTransactionType === 'buyToOpen' || position.openTransactionType === 'sellToOpen';
};

export const getDefaultClosingType = (position: UserPortfolioPosition): ClosingTradeType => {
    if (isOptionsStylePosition(position)) {
        return position.openTransactionType === 'sellToOpen' ? 'buyToClose' : 'sellToClose';
    }

    return position.direction === 'short' ? 'buyToCover' : 'sell';
};

export const getClosingTypeOptions = (position: UserPortfolioPosition): { value: ClosingTradeType; label: string }[] => {
    if (isOptionsStylePosition(position)) {
        return [
            { value: 'sellToClose', label: 'Sell To Close' },
            { value: 'buyToClose', label: 'Buy To Close' }
        ];
    }

    if (position.direction === 'short') {
        return [{ value: 'buyToCover', label: 'Buy To Cover' }];
    }

    return [{ value: 'sell', label: 'Sell' }];
};
